import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Button, IconButton, Avatar, Grid, Paper, TextField,
  Chip, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Camera, Plus, Pencil, Trash2, ArrowLeft, Heart, Image as ImageIcon } from 'lucide-react';
import { API_BASE_URL } from '../utils/api';
import { useTeam } from '../context/TeamContext';
import AdminModal from './AdminModal';
import ConfirmDeleteButton from './ConfirmDeleteButton';
import StorageBrowserModal from './StorageBrowserModal';
import { adminPanelPlaceholderSx, AdminPanelLoading, AdminPanelSkeleton, useCachedPanelCount } from './adminPanelContent';
import { useDropzone } from 'react-dropzone';
import { LinearProgress } from '@mui/material';

export default function InstaPanel({ getToken }) {
  const { activeTeamId } = useTeam();

  // ── data ──
  const [accounts, setAccounts] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cachedAccountCount, setCachedAccountCount] = useCachedPanelCount(activeTeamId ? `insta-accounts:${activeTeamId}` : null, 6);

  // ── navigation ──
  const [activeAccount, setActiveAccount] = useState(null);

  // ── account modal ──
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formUsername, setFormUsername] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formBio, setFormBio] = useState('');
  const [formCharacterId, setFormCharacterId] = useState('');
  const [formFile, setFormFile] = useState(null);
  const [formPreview, setFormPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // ── posts ──
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postTab, setPostTab] = useState(0);
  const SECTIONS = ['public', 'private', 'stories'];

  // ── add post ──
  const [addPostOpen, setAddPostOpen] = useState(false);
  const [addPostSection, setAddPostSection] = useState(0);
  const [storageBrowserOpen, setStorageBrowserOpen] = useState(false);
  const [addPostSaving, setAddPostSaving] = useState(false);

  // ── edit post ──
  const [editPostOpen, setEditPostOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postCaption, setPostCaption] = useState('');
  const [postLikes, setPostLikes] = useState(0);
  const [postSaving, setPostSaving] = useState(false);

  // ── helpers ──
  const authHeaders = useCallback(async () => {
    const token = await getToken();
    return { Authorization: `Bearer ${token}` };
  }, [getToken]);

  // ── load accounts + characters ──
  const loadAccounts = useCallback(async () => {
    if (!activeTeamId) return;
    try {
      setLoading(true);
      setError('');
      const h = await authHeaders();
      const [accRes, charRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/insta?teamId=${encodeURIComponent(activeTeamId)}`, { headers: h }),
        fetch(`${API_BASE_URL}/api/characters?teamId=${encodeURIComponent(activeTeamId)}`, { headers: h })
      ]);
      if (!accRes.ok) throw new Error('Failed to load accounts');
      if (!charRes.ok) throw new Error('Failed to load characters');
      const accData = await accRes.json();
      const charData = await charRes.json();
      const accList = accData.accounts || [];
      const charList = charData.characters || [];
      setAccounts(accList);
      setCharacters(charList);
      // Grid renders one tile per character (account or empty slot), so the
      // expected row count is the character count (or accounts if larger).
      setCachedAccountCount(Math.max(charList.length, accList.length));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeTeamId, authHeaders]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // ── load posts for active account ──
  const loadPosts = useCallback(async () => {
    if (!activeAccount) return;
    try {
      setPostsLoading(true);
      const h = await authHeaders();
      const res = await fetch(
        `${API_BASE_URL}/api/insta/${activeAccount.id}/posts?teamId=${encodeURIComponent(activeTeamId)}`,
        { headers: h }
      );
      if (!res.ok) throw new Error('Failed to load posts');
      const data = await res.json();
      setPosts(data.posts || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setPostsLoading(false);
    }
  }, [activeAccount, activeTeamId, authHeaders]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  // ── derived data ──
  const accountCharacterIds = useMemo(
    () => new Set(accounts.map((a) => a.characterId).filter(Boolean)),
    [accounts]
  );

  const emptySlots = useMemo(
    () => characters.filter((c) => !accountCharacterIds.has(c.id)),
    [characters, accountCharacterIds]
  );

  const sortedAccounts = useMemo(
    () => [...accounts].sort((a, b) => (a.username || '').localeCompare(b.username || '')),
    [accounts]
  );

  const filteredPosts = useMemo(
    () => posts.filter((p) => p.section === SECTIONS[postTab]),
    [posts, postTab]
  );

  // ── account modal logic ──
  const openCreateAccount = (characterId = '') => {
    setEditingAccount(null);
    setFormUsername('');
    setFormDisplayName('');
    setFormBio('');
    setFormCharacterId(characterId);
    setFormFile(null);
    setFormPreview(null);
    setAccountModalOpen(true);
  };

  const openEditAccount = (acc) => {
    setEditingAccount(acc);
    setFormUsername(acc.username || '');
    setFormDisplayName(acc.displayName || '');
    setFormBio(acc.bio || '');
    setFormCharacterId(acc.characterId || '');
    setFormFile(null);
    setFormPreview(acc.avatar?.url128 || acc.avatar?.url512 || null);
    setAccountModalOpen(true);
  };

  const closeAccountModal = () => {
    setAccountModalOpen(false);
    setEditingAccount(null);
    setSaving(false);
    setFormFile(null);
    setFormPreview(null);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFormFile(f);
    setFormPreview(URL.createObjectURL(f));
  };

  const saveAccount = async () => {
    if (!formUsername.trim()) return;
    setSaving(true);
    try {
      const token = await getToken();
      const fd = new FormData();
      fd.append('teamId', activeTeamId);
      fd.append('username', formUsername.trim());
      fd.append('displayName', formDisplayName.trim());
      fd.append('bio', formBio.trim());
      if (formCharacterId) fd.append('characterId', formCharacterId);
      if (formFile) fd.append('avatar', formFile);

      const url = editingAccount
        ? `${API_BASE_URL}/api/insta/${editingAccount.id}`
        : `${API_BASE_URL}/api/insta`;
      const method = editingAccount ? 'PATCH' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) throw new Error('Failed to save account');

      // If we edited the active account, update it in state
      if (editingAccount && activeAccount?.id === editingAccount.id) {
        const updated = await res.json();
        setActiveAccount(updated.account || activeAccount);
      }

      closeAccountModal();
      await loadAccounts();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async (acc) => {
    try {
      const h = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/api/insta/${acc.id}`, { method: 'DELETE', headers: h });
      if (!res.ok) throw new Error('Failed to delete account');
      if (activeAccount?.id === acc.id) setActiveAccount(null);
      closeAccountModal();
      await loadAccounts();
    } catch (e) {
      setError(e.message);
    }
  };

  // ── add post logic ──
  const openAddPost = () => {
    setAddPostSection(postTab);
    setAddPostOpen(true);
  };

  const handleStorageSelect = async (item) => {
    setStorageBrowserOpen(false);
    setAddPostSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/insta/${activeAccount.id}/posts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          teamId: activeTeamId,
          section: SECTIONS[addPostSection],
          imageUrl: item.url,
          imageName: item.name,
          imagePreview512: item.preview512 || item.url,
          storagePath: item.path,
          caption: ''
        })
      });
      if (!res.ok) throw new Error('Failed to create post');
      setAddPostOpen(false);
      await loadPosts();
    } catch (e) {
      setError(e.message);
    } finally {
      setAddPostSaving(false);
    }
  };

  // ── drag-and-drop bulk upload ──
  const [dropUploading, setDropUploading] = useState(false);

  const handleDropFiles = useCallback(async (acceptedFiles) => {
    if (!activeAccount || !acceptedFiles.length) return;
    const images = acceptedFiles.filter((f) => f.type.startsWith('image/'));
    if (!images.length) return;
    setDropUploading(true);
    try {
      const token = await getToken();
      const section = SECTIONS[postTab];
      const parentPath = `Insta/${activeAccount.username}/${section}`;

      // 1. Upload all files to Storage
      const fd = new FormData();
      fd.append('parentPath', parentPath);
      fd.append('teamId', activeTeamId);
      for (const f of images) fd.append('files', f);

      const uploadRes = await fetch(`${API_BASE_URL}/api/storage/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (!uploadRes.ok) throw new Error('Upload failed');
      const uploaded = await uploadRes.json();
      const items = uploaded.items || uploaded.files || [];

      // 2. Create a post for each uploaded file
      for (const item of items) {
        await fetch(`${API_BASE_URL}/api/insta/${activeAccount.id}/posts`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            teamId: activeTeamId,
            section,
            imageUrl: item.publicUrl || item.url || '',
            imageName: item.name || '',
            imagePreview512: item.previewUrls?.s512 || item.publicUrl || item.url || '',
            storagePath: `${parentPath}/${item.name || ''}`,
            caption: ''
          })
        });
      }
      await loadPosts();
    } catch (e) {
      setError(e.message);
    } finally {
      setDropUploading(false);
    }
  }, [activeAccount, postTab, activeTeamId, getToken, loadPosts]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDropFiles,
    noClick: true,
    noKeyboard: true,
    accept: { 'image/*': [] }
  });

  // ── edit post logic ──
  const openEditPost = (post) => {
    setEditingPost(post);
    setPostCaption(post.caption || '');
    setPostLikes(post.likes || 0);
    setEditPostOpen(true);
  };

  const closeEditPost = () => {
    setEditPostOpen(false);
    setEditingPost(null);
    setPostSaving(false);
  };

  const savePost = async () => {
    if (!editingPost) return;
    setPostSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/insta/${activeAccount.id}/posts/${editingPost.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          teamId: activeTeamId,
          caption: postCaption,
          likes: Number(postLikes) || 0
        })
      });
      if (!res.ok) throw new Error('Failed to save post');
      closeEditPost();
      await loadPosts();
    } catch (e) {
      setError(e.message);
    } finally {
      setPostSaving(false);
    }
  };

  const deletePost = async (post) => {
    try {
      const h = await authHeaders();
      const res = await fetch(
        `${API_BASE_URL}/api/insta/${activeAccount.id}/posts/${post.id}`,
        { method: 'DELETE', headers: h }
      );
      if (!res.ok) throw new Error('Failed to delete post');
      await loadPosts();
      closeEditPost();
    } catch (e) {
      setError(e.message);
    }
  };

  // ── drag reorder posts ──
  const dragPostRef = useRef(null);

  const handlePostDragStart = (e, post) => {
    dragPostRef.current = post;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handlePostDrop = async (e, targetPost) => {
    e.preventDefault();
    const src = dragPostRef.current;
    dragPostRef.current = null;
    if (!src || src.id === targetPost.id) return;
    // Reorder: move src to target's position in filteredPosts
    const ordered = [...filteredPosts];
    const srcIdx = ordered.findIndex((p) => p.id === src.id);
    const tgtIdx = ordered.findIndex((p) => p.id === targetPost.id);
    if (srcIdx < 0 || tgtIdx < 0) return;
    ordered.splice(srcIdx, 1);
    ordered.splice(tgtIdx, 0, src);
    // Patch order for all posts in this section
    try {
      const token = await getToken();
      await Promise.all(ordered.map((p, i) =>
        fetch(`${API_BASE_URL}/api/insta/${activeAccount.id}/posts/${p.id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ order: i })
        })
      ));
      await loadPosts();
    } catch (_) {}
  };

  // ── character name lookup ──
  const charName = useCallback(
    (charId) => characters.find((c) => c.id === charId)?.name || '',
    [characters]
  );

  // ════════════════════════════════════════════════
  // VIEW 2: Account Detail
  // ════════════════════════════════════════════════
  if (activeAccount) {
    return (
      <Box {...getRootProps()} sx={{ position: 'relative' }}>
        <input {...getInputProps()} className="insta-drop-input" />
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconButton size="small" onClick={() => setActiveAccount(null)} sx={{ color: 'text.secondary' }}>
              <ArrowLeft size={20} />
            </IconButton>
            <Avatar
              src={activeAccount.avatar?.url128 || activeAccount.avatar?.url64 || undefined}
              sx={{ width: 36, height: 36, bgcolor: 'rgba(139, 92, 246, 0.14)', color: 'primary.light' }}
            >
              {(activeAccount.username || '?')[0]?.toUpperCase()}
            </Avatar>
            <Typography variant="h6" fontWeight={600} color="text.primary" noWrap>
              {activeAccount.username}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={openAddPost}>
              Add post
            </Button>
            <Button variant="outlined" size="small" startIcon={<Pencil size={14} />} onClick={() => openEditAccount(activeAccount)}>
              Edit Account
            </Button>
          </Box>
        </Box>

        {error && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
            <Typography variant="body2" color="error" onClick={() => setError('')} sx={{ cursor: 'pointer' }}>
              {error}
            </Typography>
          </Box>
        )}

        {/* Drag overlay */}
        {isDragActive && (
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(217, 70, 239, 0.10)', border: '2px dashed', borderColor: '#d946ef', borderRadius: 2, zIndex: 20
          }}>
            <Typography variant="h6" sx={{ color: '#d946ef', fontWeight: 600 }}>
              Drop images to create {SECTIONS[postTab]} posts
            </Typography>
          </Box>
        )}

        {dropUploading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Uploading and creating posts...
            </Typography>
            <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#d946ef' } }} />
          </Box>
        )}

        {/* Post tabs */}
        <Tabs
          value={postTab}
          onChange={(_, v) => setPostTab(v)}
          sx={{
            mb: 2,
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 500, fontSize: '0.85rem' }
          }}
        >
          <Tab label="Public" />
          <Tab label="Private" />
          <Tab label="Stories" />
        </Tabs>

        {/* Posts grid */}
        {postsLoading ? (
          <AdminPanelLoading label="Loading posts..." />
        ) : filteredPosts.length === 0 ? (
          <Box
            onClick={() => document.querySelector('.insta-drop-input')?.click()}
            sx={{ ...adminPanelPlaceholderSx, cursor: 'pointer', border: '1px dashed rgba(255,255,255,0.15)', borderRadius: 2, '&:hover': { borderColor: 'rgba(217,70,239,0.4)', bgcolor: 'rgba(217,70,239,0.04)' } }}
          >
            <ImageIcon size={28} style={{ opacity: 0.4 }} />
            <Typography variant="body2" color="text.secondary">
              Drop images here or click to browse
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={0.5}>
            {filteredPosts.map((post) => (
              <Grid item xs={4} key={post.id}>
                <Box
                  draggable
                  onDragStart={(e) => handlePostDragStart(e, post)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handlePostDrop(e, post)}
                  onClick={() => openEditPost(post)}
                  sx={{
                    position: 'relative',
                    paddingTop: '100%',
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: 'grab',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    backgroundImage: `url(${post.imagePreview512 || post.imageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    transition: 'opacity 0.15s',
                    '&:hover': { opacity: 0.85 },
                    '&:active': { cursor: 'grabbing' }
                  }}
                >
                  {/* Info overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      px: 0.75,
                      py: 0.5,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))'
                    }}
                  >
                    {post.caption && (
                      <Typography sx={{ color: '#fff', fontSize: '0.6rem', lineHeight: 1.2, mb: 0.25, opacity: 0.85 }} noWrap>
                        {post.caption}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Heart size={11} style={{ color: '#fff', opacity: 0.9 }} />
                      <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500, fontSize: '0.65rem' }}>
                        {post.likes || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Add Post Modal */}
        <AdminModal
          open={addPostOpen}
          onClose={() => setAddPostOpen(false)}
          title="Add post"
          maxWidth="xs"
          actions={
            <Button onClick={() => setAddPostOpen(false)} disabled={addPostSaving}>
              Cancel
            </Button>
          }
        >
          <Box display="flex" flexDirection="column" gap={2}>
            <Tabs
              value={addPostSection}
              onChange={(_, v) => setAddPostSection(v)}
              sx={{
                minHeight: 32,
                '& .MuiTab-root': { minHeight: 32, textTransform: 'none', fontWeight: 500, fontSize: '0.82rem' }
              }}
            >
              <Tab label="Public" />
              <Tab label="Private" />
              <Tab label="Stories" />
            </Tabs>
            <Button
              variant="outlined"
              startIcon={<ImageIcon size={16} />}
              onClick={() => setStorageBrowserOpen(true)}
              disabled={addPostSaving}
            >
              {addPostSaving ? 'Creating post...' : 'Pick image from Storage'}
            </Button>
          </Box>
        </AdminModal>

        {/* Storage Browser for picking post image */}
        <StorageBrowserModal
          open={storageBrowserOpen}
          onClose={() => setStorageBrowserOpen(false)}
          onSelect={handleStorageSelect}
          fileFilter="image"
          teamId={activeTeamId}
          getToken={getToken}
          title="Select post image"
        />

        {/* Edit Post Modal */}
        <AdminModal
          open={editPostOpen}
          onClose={closeEditPost}
          title="Edit post"
          maxWidth="xs"
          actions={
            <>
              <ConfirmDeleteButton onConfirm={() => editingPost && deletePost(editingPost)} iconSize={16} />
              <Box sx={{ flex: 1 }} />
              <Button onClick={closeEditPost} disabled={postSaving}>Cancel</Button>
              <Button variant="contained" onClick={savePost} disabled={postSaving}>
                {postSaving ? 'Saving...' : 'Save'}
              </Button>
            </>
          }
        >
          {editingPost && (
            <Box display="flex" flexDirection="column" gap={2}>
              <Box
                sx={{
                  width: '100%',
                  paddingTop: '100%',
                  borderRadius: 1,
                  backgroundImage: `url(${editingPost.imagePreview512 || editingPost.imageUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  bgcolor: 'rgba(255,255,255,0.04)'
                }}
              />
              <TextField
                label="Caption"
                size="small"
                fullWidth
                multiline
                rows={3}
                value={postCaption}
                onChange={(e) => setPostCaption(e.target.value)}
              />
              <TextField
                label="Likes"
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
                value={postLikes}
                onChange={(e) => setPostLikes(e.target.value)}
              />
            </Box>
          )}
        </AdminModal>

        {/* Account Modal (edit from detail view) */}
        <AdminModal
          open={accountModalOpen}
          onClose={closeAccountModal}
          title={editingAccount ? 'Edit account' : 'New account'}
          maxWidth="xs"
          actions={
            <Box display="flex" justifyContent="space-between" width="100%">
              {editingAccount ? (
                <ConfirmDeleteButton onConfirm={() => deleteAccount(editingAccount)} iconSize={16} />
              ) : (
                <Box />
              )}
              <Box display="flex" gap={1}>
                <Button onClick={closeAccountModal} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={saveAccount} disabled={!formUsername.trim() || saving}>
                  {saving ? 'Saving...' : editingAccount ? 'Save' : 'Create'}
                </Button>
              </Box>
            </Box>
          }
        >
          <AccountForm
            formPreview={formPreview}
            formUsername={formUsername}
            setFormUsername={setFormUsername}
            formDisplayName={formDisplayName}
            setFormDisplayName={setFormDisplayName}
            formBio={formBio}
            setFormBio={setFormBio}
            formCharacterId={formCharacterId}
            charName={charName}
            fileRef={fileRef}
            handleFileChange={handleFileChange}
          />
        </AdminModal>
      </Box>
    );
  }

  // ════════════════════════════════════════════════
  // VIEW 1: Account List
  // ════════════════════════════════════════════════
  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ color: '#d946ef', display: 'flex', alignItems: 'center' }}>
            <Camera size={22} strokeWidth={2} />
          </Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Insta
          </Typography>
        </Box>
        <Button variant="contained" size="small" startIcon={<Plus size={16} />} disabled title="Create accounts from character slots below">
          Add account
        </Button>
      </Box>

      {error && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
          <Typography variant="body2" color="error" onClick={() => setError('')} sx={{ cursor: 'pointer' }}>
            {error}
          </Typography>
        </Box>
      )}

      {loading ? (
        <AdminPanelSkeleton variant="grid" count={cachedAccountCount} />
      ) : emptySlots.length === 0 && accounts.length === 0 ? (
        <Box sx={adminPanelPlaceholderSx}>
          <Typography variant="body2" color="text.secondary">No accounts yet.</Typography>
        </Box>
      ) : (
        <Grid container spacing={1.5}>
          {/* Empty slots: characters without accounts */}
          {emptySlots.map((c) => (
            <Grid item xs={12} sm={6} md={4} key={`slot-${c.id}`}>
              <Paper
                variant="outlined"
                onClick={() => openCreateAccount(c.id)}
                sx={{
                  p: 2,
                  border: '1px dashed rgba(255,255,255,0.15)',
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: 'rgba(139,92,246,0.4)', bgcolor: 'rgba(139,92,246,0.04)' }
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar
                    src={c.avatar?.url128 || c.avatar?.url64 || undefined}
                    sx={{ width: 48, height: 48, bgcolor: 'rgba(139, 92, 246, 0.14)', color: 'primary.light', opacity: 0.6 }}
                  >
                    {c.name[0]?.toUpperCase()}
                  </Avatar>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ opacity: 0.7 }}>
                      {c.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      No account
                    </Typography>
                  </Box>
                  <Plus size={16} style={{ opacity: 0.35 }} />
                </Box>
              </Paper>
            </Grid>
          ))}

          {/* Filled accounts */}
          {sortedAccounts.map((acc) => (
            <Grid item xs={12} sm={6} md={4} key={acc.id}>
              <Paper
                variant="outlined"
                onClick={() => setActiveAccount(acc)}
                sx={{
                  p: 2,
                  borderColor: 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: 'rgba(139,92,246,0.4)', bgcolor: 'rgba(139,92,246,0.04)' }
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar
                    src={acc.avatar?.url128 || acc.avatar?.url64 || undefined}
                    sx={{ width: 48, height: 48, bgcolor: 'rgba(139, 92, 246, 0.14)', color: 'primary.light' }}
                  >
                    {(acc.username || '?')[0]?.toUpperCase()}
                  </Avatar>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="subtitle2" fontWeight={600} noWrap>
                      {acc.username}
                    </Typography>
                    {acc.bio && (
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {acc.bio}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={`${Object.values(acc.postCounts || {}).reduce((s, n) => s + n, 0)} posts`}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 22, bgcolor: 'rgba(139,92,246,0.12)', color: 'primary.light' }}
                  />
                  <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => openEditAccount(acc)} sx={{ color: 'text.secondary' }}>
                      <Pencil size={14} />
                    </IconButton>
                    <ConfirmDeleteButton onConfirm={() => deleteAccount(acc)} />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Account Create/Edit Modal */}
      <AdminModal
        open={accountModalOpen}
        onClose={closeAccountModal}
        title={editingAccount ? 'Edit account' : 'New account'}
        maxWidth="xs"
        actions={
          <Box display="flex" justifyContent="space-between" width="100%">
            {editingAccount ? (
              <ConfirmDeleteButton onConfirm={() => deleteAccount(editingAccount)} iconSize={16} />
            ) : (
              <Box />
            )}
            <Box display="flex" gap={1}>
              <Button onClick={closeAccountModal} disabled={saving}>Cancel</Button>
              <Button variant="contained" onClick={saveAccount} disabled={!formUsername.trim() || saving}>
                {saving ? 'Saving...' : editingAccount ? 'Save' : 'Create'}
              </Button>
            </Box>
          </Box>
        }
      >
        <AccountForm
          formPreview={formPreview}
          formUsername={formUsername}
          setFormUsername={setFormUsername}
          formDisplayName={formDisplayName}
          setFormDisplayName={setFormDisplayName}
          formBio={formBio}
          setFormBio={setFormBio}
          formCharacterId={formCharacterId}
          charName={charName}
          fileRef={fileRef}
          handleFileChange={handleFileChange}
        />
      </AdminModal>
    </Box>
  );
}

/**
 * Shared account form used in both list-view and detail-view modals.
 */
function AccountForm({
  formPreview,
  formUsername,
  setFormUsername,
  formDisplayName,
  setFormDisplayName,
  formBio,
  setFormBio,
  formCharacterId,
  charName,
  fileRef,
  handleFileChange
}) {
  return (
    <Box display="flex" flexDirection="column" gap={2} alignItems="center">
      <Avatar
        src={formPreview || undefined}
        sx={{
          width: 96,
          height: 96,
          cursor: 'pointer',
          bgcolor: 'rgba(139, 92, 246, 0.14)',
          color: 'primary.light',
          fontSize: '2rem'
        }}
        onClick={() => fileRef.current?.click()}
      >
        {formUsername ? formUsername[0]?.toUpperCase() : '?'}
      </Avatar>
      <Button size="small" variant="outlined" onClick={() => fileRef.current?.click()}>
        {formPreview ? 'Change avatar' : 'Upload avatar'}
      </Button>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFileChange} />

      {formCharacterId && (
        <Chip
          label={`Character: ${charName(formCharacterId) || formCharacterId}`}
          size="small"
          sx={{ bgcolor: 'rgba(139,92,246,0.12)', color: 'primary.light' }}
        />
      )}

      <TextField
        label="Username"
        size="small"
        fullWidth
        value={formUsername}
        onChange={(e) => setFormUsername(e.target.value)}
        autoFocus
      />
      <TextField
        label="Display Name"
        size="small"
        fullWidth
        value={formDisplayName}
        onChange={(e) => setFormDisplayName(e.target.value)}
      />
      <TextField
        label="Bio"
        size="small"
        fullWidth
        multiline
        rows={2}
        value={formBio}
        onChange={(e) => setFormBio(e.target.value)}
      />
    </Box>
  );
}
